import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type ResumeCampaignResponse,
  resumeCampaignResponseSchema,
} from '@borradh-workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ListCampaignsResponse } from '../types';

/**
 * Resume campaign hook
 * Resumes (activates) a paused campaign on Meta Ads
 */
export const useResumeCampaign = (
  toastOnSuccess = true,
  toastOnError = true
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (campaignId: string) => {
      return apiClient.post<ResumeCampaignResponse>(
        `meta-campaigns/${campaignId}/resume`,
        undefined,
        { schema: resumeCampaignResponseSchema }
      );
    },
    onMutate: async (campaignId) => {
      const campaignsKey = queryKeys.metaAds.campaigns();
      await queryClient.cancelQueries({ queryKey: campaignsKey });
      const previous = queryClient.getQueriesData<ListCampaignsResponse>({
        queryKey: campaignsKey,
      });
      queryClient.setQueriesData<ListCampaignsResponse>(
        { queryKey: campaignsKey },
        (old) => {
          if (!old?.campaigns) return old;
          return {
            ...old,
            campaigns: old.campaigns.map((c) =>
              c.id === campaignId
                ? { ...c, status: 'ACTIVE', effectiveStatus: 'ACTIVE' }
                : c
            ),
          };
        }
      );
      return { previous };
    },
    onSuccess: (_, campaignId) => {
      void queryClient.invalidateQueries({ queryKey: ['meta-campaigns'] });
      void queryClient.invalidateQueries({
        queryKey: ['meta-campaigns', campaignId],
      });
      if (toastOnSuccess) toast.success('Campaign resumed');
    },
    onError: (error, _campaignId, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      if (toastOnError)
        toast.error(`Failed to resume campaign: ${error.message}`);
    },
  });

  return {
    ...mutation,
    execute: mutation.mutate,
    executeAsync: mutation.mutateAsync,
    isExecuting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
